if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.setupVariantPreviews();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if ((this.dataset.desktopLayout.includes('thumbnail')) && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
              if (this.elements.thumbnails.resetPages) this.elements.thumbnails.resetPages();
            }
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (options.skipPageScroll || activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail && options.announce !== false) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setupVariantPreviews() {
        const productSection = this.closest('section');
        const variantPicker = productSection?.querySelector('variant-selects[data-section]');
        this.variantData = JSON.parse(this.querySelector('[data-hauck-gallery-variants]')?.textContent || '[]');
        this.duplicateGroups = JSON.parse(this.querySelector('[data-hauck-gallery-duplicates]')?.textContent || '[]');
        const selectedVariant = () => variantPicker?.querySelector('[type="application/json"]')
          ? this.getVariantFromPicker(variantPicker)
          : this.variantData[0];
        this.filterMediaByVariant(selectedVariant(), variantPicker);
        if (!variantPicker) return;

        // Only committed selections change the product gallery. Hovering or
        // tabbing over a swatch must not display an unselected colour.
        variantPicker.addEventListener('change', () => {
          window.queueMicrotask(() => {
            this.restoreSwatchNames(variantPicker);
            this.filterMediaByVariant(selectedVariant(), variantPicker);
          });
        });
      }

      getVariantFromPicker(variantPicker) {
        const options = variantPicker.getVariantData()[0]?.options?.slice() || [];
        variantPicker.querySelectorAll('[data-option-position]').forEach((option) => {
          const optionIndex = Number(option.dataset.optionPosition) - 1;
          options[optionIndex] =
            option.tagName === 'SELECT' ? option.value : option.querySelector('input:checked')?.value;
        });
        return variantPicker
          .getVariantData()
          .find((variant) => variant.options.every((value, index) => value === options[index]));
      }

      updateSwatchName(variantPicker, optionName, optionValue) {
        const selectedName = Array.from(variantPicker.querySelectorAll('[data-selected-swatch-value]')).find(
          (element) => element.dataset.selectedSwatchValue === optionName
        );
        if (selectedName) selectedName.textContent = optionValue;
      }

      restoreSwatchNames(variantPicker) {
        variantPicker.querySelectorAll('fieldset[data-option-position]').forEach((fieldset) => {
          const checkedInput = fieldset.querySelector('input:checked');
          if (checkedInput) this.updateSwatchName(variantPicker, checkedInput.name, checkedInput.value);
        });
      }

      filterMediaByVariant(variant, variantPicker) {
        // Limit filtering to gallery slides. Image zoom buttons also carry a
        // data-media-id and must not count toward the visible-media fallback.
        const mediaItems = Array.from(
          this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')
        );
        if (!mediaItems.length) return;

        const variantData = this.variantData;
        const sectionId = variantPicker?.dataset.section || this.closest('section')?.dataset.section;
        const normalizeSku = (value) => String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
        const variantSkus = [...new Set(variantData.map((item) => normalizeSku(item.sku)).filter(Boolean))].sort(
          (a, b) => b.length - a.length
        );
        const mediaSku = new Map(
          mediaItems.map((item) => {
            const normalizedFilename = normalizeSku(item.dataset.mediaFilename);
            return [item.dataset.mediaId, variantSkus.find((sku) => normalizedFilename.startsWith(sku))];
          })
        );
        const selectedSku = normalizeSku(variant?.sku);
        const selectedItem = variant?.featured_media?.id
          ? mediaItems.find(
              (item) => item.dataset.mediaId === `${sectionId}-${variant.featured_media.id}`
            )
          : null;
        const skuMatchedItems = selectedSku
          ? mediaItems.filter((item) => mediaSku.get(item.dataset.mediaId) === selectedSku)
          : [];

        // Never fall back to another colour's pictures when the selected
        // variant has no SKU-labelled supporting gallery.
        let visibleItems = mediaItems;
        if (variantData.length > 1 && variant) {
          visibleItems = selectedItem
            ? [selectedItem, ...skuMatchedItems.filter((item) => item !== selectedItem)]
            : skuMatchedItems;
          if (!visibleItems.length) {
            const otherFeaturedIds = new Set(variantData.map((item) => String(item.featured_media?.id)));
            visibleItems = mediaItems.filter((item) =>
              !mediaSku.get(item.dataset.mediaId) &&
              !otherFeaturedIds.has(item.dataset.mediaId.split('-').pop())
            ).slice(0, 1);
          }
        } else if (selectedItem) {
          visibleItems = [selectedItem, ...mediaItems.filter((item) => item !== selectedItem)];
        }

        // Explicit, merchant-verified colour mappings take precedence over SKU filenames.
        const galleryMap = JSON.parse(this.querySelector('[data-hauck-gallery-map]')?.textContent || '{}');
        const explicitIds = galleryMap[String(variant?.id)];
        if (Array.isArray(explicitIds) && explicitIds.length) {
          const mappedItems = explicitIds.map((id) => mediaItems.find((item) =>
            item.dataset.mediaId === `${sectionId}-${id}`
          )).filter(Boolean);
          if (mappedItems.length) visibleItems = selectedItem && mappedItems.includes(selectedItem)
            ? [selectedItem, ...mappedItems.filter((item) => item !== selectedItem)]
            : mappedItems;
        }

        // Preserve the selected variant's assigned primary image, then remove
        // verified duplicate photos from slides, thumbnails and the zoom modal.
        // Product media is not deleted or reordered in Shopify.
        const seenImages = new Set();
        visibleItems = visibleItems.filter((item) => {
          const rawId = item.dataset.mediaId.split('-').pop();
          const duplicateGroup = this.duplicateGroups.find((group) => group.includes(rawId));
          const imageKey = duplicateGroup ? duplicateGroup[0] : (item.dataset.mediaFilename || rawId);
          if (seenImages.has(imageKey)) return false;
          seenImages.add(imageKey);
          return true;
        });

        const visibleMediaIds = new Set(visibleItems.map((item) => item.dataset.mediaId));
        mediaItems.forEach((item) => item.toggleAttribute('hidden', !visibleMediaIds.has(item.dataset.mediaId)));

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
            thumbnail.toggleAttribute('hidden', !visibleMediaIds.has(thumbnail.dataset.target));
          });
          if (this.elements.thumbnails.resetPages) this.elements.thumbnails.resetPages();
        }
        if (this.elements.viewer.resetPages) this.elements.viewer.resetPages();

        const activeItem = mediaItems.find((item) => item.classList.contains('is-active'));
        const changedVariant = this.activeVariantId !== variant?.id;
        this.activeVariantId = variant?.id;
        if (visibleItems[0] && (changedVariant || !activeItem || activeItem.hidden)) {
          this.setActiveMedia(visibleItems[0].dataset.mediaId, false, { skipPageScroll: true, announce: false });
        }

        const counter = this.querySelector('.slider-counter--total');
        if (counter) counter.textContent = visibleItems.length;

        const modal = document.querySelector(`#ProductModal-${sectionId}`);
        if (modal) {
          const rawVisibleIds = new Set(Array.from(visibleMediaIds, (id) => id.split('-').pop()));
          modal.querySelectorAll('[data-media-id]').forEach((item) => {
            item.toggleAttribute('hidden', !rawVisibleIds.has(String(item.dataset.mediaId)));
          });
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
